/**
 * Model references the agent wrote into its answer, lifted out of the prose.
 *
 * The agent names a model as `[HG Customer Fit (57)](studio:model/57)`, which is a
 * rule about what its sentence contains rather than a tool it has to remember to call
 * at the right moment. Measured, it called that tool zero times and wrote the link
 * every time.
 *
 * Rendered inline, that link is a few blue words in the middle of a paragraph, which
 * is not what "open this model and take my question with you" deserves to look like.
 * So the label stays in the sentence as plain text and the reference becomes a card
 * under the answer.
 */

/** `[label](studio:model/<id>)`, the shape the agent is asked to write. */
const MODEL_LINK = /\[([^\]\n]+)\]\(studio:model\/([A-Za-z0-9_-]+)\)/g;

export interface ModelLink {
  id: string;
  label: string;
}

export interface SplitAnswer {
  /** The answer with each link reduced to its own label. */
  prose: string;
  /** Every model named, first mention first, each appearing once. */
  models: ModelLink[];
}

/**
 * Separate the prose from the models it names.
 *
 * Only COMPLETE links are touched. An answer arrives a token at a time, so half of
 * one is on screen for a moment on nearly every turn, and a regex loose enough to
 * catch `[HG Customer Fit (57)](studio:mod` would eat the label and then have nothing
 * to put back when the rest arrived.
 */
export function splitModelLinks(text: string): SplitAnswer {
  const models: ModelLink[] = [];
  const seen = new Set<string>();
  const prose = text.replace(
    MODEL_LINK,
    (_whole, label: string, id: string) => {
      if (!seen.has(id)) {
        seen.add(id);
        models.push({ id, label: label.trim() });
      }
      return label;
    }
  );
  return { prose, models };
}
